const _ = require("lodash");
const CanvasUtil = require("../../util/CanvasUtil");
const ArrayUtil = require("../../util/ArrayUtil");
const Math2D = require("../../util/Math2D");
const colors = require("../../util/ColorUtil").getCurrentScheme();

// The aspect ratio of the given plot height/width will be maintained and the plot will be centered as necessary
// ctx: context to draw upon
// x, y: top left coordinates
// w, h: width and height
module.exports = function(
	ctx,
	x,
	y,
	w,
	h,
	gridSize,
	removedSquares,
	numberOfShapes,
	shadedSquares,
	maxShapesPerRow
) {
	const xSpacing = 0.01 * w;
	const ySpacing = 0.01 * h;
	if (
		!gridSize ||
		gridSize.length !== 2 ||
		_.every(gridSize, dir => {
			const val = parseInt(dir);
			return _.isNaN(val) || val < 1;
		})
	) {
		gridSize = [1, 1];
	}
	numberOfShapes = numberOfShapes || 1;
	maxShapesPerRow = Math.max(maxShapesPerRow || numberOfShapes, 1);
	const numSquareRows = gridSize[1];
	const numSquareCols = gridSize[0];
	const numSquares = numSquareRows * numSquareCols;
	const removed = _.map(
		_.filter(removedSquares, sq => sq.length === 2),
		rsq => {
			return [rsq[0], numSquareRows - rsq[1] - 1];
		}
	);
	const maxSubviewCols = Math.min(numberOfShapes, maxShapesPerRow);
	const maxSubviewRows = Math.ceil(numberOfShapes / maxShapesPerRow);
	const subviews = [];

	let shaded = [];
	if (shadedSquares.length) {
		const ind = _.range(0, numberOfShapes);
		shaded = _.map(ind, i => {
			return !shadedSquares[i]
				? []
				: _.filter(shadedSquares[i], el => {
						return (
							_.isFinite(el) && el > 0 && el <= numSquares - removed.length
						);
				  });
		});
	}

	const computeGridDimension = function(rect) {
		const margin = 0.25;
		// Dim-independent size of grid, as determined by core.
		const baseW = numSquareCols;
		const baseH = numSquareRows;
		// Size to use if width were the limiting factor
		const wSize = rect.w / (baseW + 2 * margin);
		// Size to use if height were the limiting factor
		const hSize = rect.h / (baseH + 2 * margin);
		// Actual size to use
		const size = Math.min(wSize, hSize);
		// Determine the width and height occupied by a grid of this size, with dim
		// accounted for.
		const fullW = baseW * size;
		const fullH = baseH * size;
		return {
			x: rect.x + (rect.w - fullW) / 2,
			y: rect.y + (rect.h - fullH) / 2,
			size,
		};
	};

	const getSubviewMeasure = function(width) {
		const geomSize = computeGridDimension({x: 0, y: 0, w: width, h: Infinity})
			.size;
		return {
			w: geomSize * numSquareCols + geomSize / 2,
			h: geomSize * numSquareRows + geomSize / 2,
		};
	};

	// Based on measure function from GridContainer.js
	const getGridMeasure = function() {
		const subviewWidth = Math.max(
			0,
			(w - (maxSubviewCols - 1) * xSpacing) / maxSubviewCols
		);
		let totalSubviewHeight = 0;
		const subviewsMeasure = getSubviewMeasure(subviewWidth);
		for (let i = 0; i < numberOfShapes; i++) {
			if ((i + 1) % maxSubviewCols === 0 || i === numberOfShapes - 1) {
				totalSubviewHeight += subviewsMeasure.h;
				totalSubviewHeight += ySpacing;
			}
		}
		totalSubviewHeight -= ySpacing;
		const totalWidth =
			(subviewsMeasure.w + xSpacing) * maxSubviewCols - xSpacing;
		return {
			w: totalWidth,
			h: totalSubviewHeight,
			subviewsMeasure,
			fullSubviewWidth: subviewWidth,
		};
	};

	// Based on layout function from GridContainer.js
	const generateAllSubViewRects = function() {
		const measure = getGridMeasure();
		let heightShrink = measure.h ? h / measure.h : 0;
		if (heightShrink > 1) {
			heightShrink = 1;
		}
		const fullSubviewWidth = measure.fullSubviewWidth;
		const subviewMeasure = measure.subviewsMeasure;

		let rowY = y;
		const totalRowHeights =
			(maxSubviewRows * subviewMeasure.h + (maxSubviewRows - 1) * ySpacing) *
			heightShrink;
		rowY += (h - totalRowHeights) / 2;
		for (let i = 0; i < numberOfShapes; i += 1) {
			const colNum = i % maxSubviewCols;
			const rowNum = Math.floor(i / maxSubviewCols);
			if (colNum === 0 && rowNum !== 0) {
				rowY += heightShrink * (subviewMeasure.h + (i > 0 ? ySpacing : 0));
			}
			let subviewX;
			if (
				numberOfShapes % maxSubviewCols !== 0 &&
				i >= Math.floor(numberOfShapes / maxSubviewCols) * maxSubviewCols
			) {
				subviewX =
					x +
					colNum * (fullSubviewWidth + xSpacing) +
					((maxSubviewCols - (numberOfShapes % maxSubviewCols)) *
						(fullSubviewWidth + xSpacing)) /
						2;
			} else {
				subviewX = x + colNum * (fullSubviewWidth + xSpacing);
			}
			subviewX += (fullSubviewWidth - subviewMeasure.w) / 2;
			const subviewY = rowY;
			subviews.push({
				x: subviewX,
				y: subviewY,
				w: subviewMeasure.w,
				h: heightShrink * subviewMeasure.h,
			});
		}
	};
	const getCellRect = function(dim, crd) {
		return {
			x: dim.x + crd[0] * dim.size,
			y: dim.y + crd[1] * dim.size,
			w: dim.size,
			h: dim.size,
		};
	};
	const getBoundaryDrawVertices = function(dim, crd) {
		let vertices = [];
		const cellEdges = [
			[crd[0], crd[1], true],
			[crd[0] + 1, crd[1], false],
			[crd[0], crd[1] + 1, true],
			[crd[0], crd[1], false],
		];
		const edgeVertices = _.map(cellEdges, edge => {
			return [
				[edge[0], edge[1]],
				[edge[0] + (edge[2] ? 1 : 0), edge[1] + (edge[2] ? 0 : 1)],
			];
		});
		const drawVertices = _.map(edgeVertices, verts =>
			_.map(verts, v => {
				return [dim.x + v[0] * dim.size, dim.y + v[1] * dim.size];
			})
		);
		const isInGrid = c =>
			c[0] >= 0 && c[0] < gridSize[0] && c[1] >= 0 && c[1] < gridSize[1];
		if (!ArrayUtil.isElInArray(removed, crd)) {
			const adjCells = [
				[crd[0], crd[1] - 1],
				[crd[0] + 1, crd[1]],
				[crd[0], crd[1] + 1],
				[crd[0] - 1, crd[1]],
			];
			const bdryFlag = _.map(
				adjCells,
				cell => !isInGrid(cell) || ArrayUtil.isElInArray(removed, cell)
			);
			vertices = _.filter(drawVertices, (v, i) => bdryFlag[i]);
		}
		return vertices;
	};
	const renderSubview = function(rect, shapeIndex) {
		const dim = computeGridDimension(rect);
		ctx.save();
		ctx.lineWidth *= 1.5;
		let linearSquareI = 1;
		for (let i = 0; i < numSquareRows; ++i) {
			for (let j = 0; j < numSquareCols; ++j) {
				const crd = [j, i];
				if (!ArrayUtil.isElInArray(removed, crd)) {
					const drawRect = getCellRect(dim, crd);
					if (_.includes(shaded[shapeIndex], linearSquareI++)) {
						ctx.fillStyle = colors.inputContrastFill;
					} else {
						ctx.fillStyle = colors.defaultWhiteFill;
					}
					ctx
						.bp()
						.rect(...Math2D.rectToArray(drawRect))
						.fill();
					ctx.strokeStyle = colors.gridLine;
					ctx.stroke();
				}
			}
		}
		ctx.restore();
		ctx.save();
		ctx.lineWidth *= 2.0;
		for (let i = 0; i < numSquareCols; ++i) {
			for (let j = 0; j < numSquareRows; ++j) {
				const crd = [i, j];
				const drawVertices = getBoundaryDrawVertices(dim, crd);
				if (drawVertices.length) {
					ctx.strokeStyle = colors.defaultText;
					ctx.lineCap = "round";
					ctx.beginPath();
					_.each(drawVertices, v => {
						ctx.M(...v[0]).L(...v[1]);
					});
					ctx.stroke();
				}
			}
		}
		ctx.restore();
	};
	generateAllSubViewRects();
	_.each(subviews, (rect, i) => {
		ctx.lineWidth = CanvasUtil.getOptimalLineWidth(ctx);
		//		ctx.strokeRect(...Math2D.rectToArray(rect));
		renderSubview(rect, i);
	});
};
